import type { GetTagsResponse } from '@responses/tags';
import { createTag, deleteTag, updateTag } from '@src/mutations';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { getRequestAuthPayload } from '../helpers/getRequestAuth';

import type { QueryFactory } from './factory.types';

export const fetchTags = async (): Promise<GetTagsResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/tags',
  });

  const response = await axios(networkPayload);
  return response.data;
};

export const tagQueries = {
  list: () =>
    queryOptions({
      queryKey: ['tags'] as const,
      queryFn: fetchTags,
    }),
} satisfies QueryFactory;

export function useTags() {
  return useQuery(tagQueries.list());
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ label }: { label: string }) => createTag({ label }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tagQueries.list().queryKey }),
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) => updateTag(id, { label }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tagQueries.list().queryKey }),
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTag(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tagQueries.list().queryKey }),
  });
}
